import React, { useEffect, useMemo, useState } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import CircularProgress from '@material-ui/core/CircularProgress';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import Chip from '@material-ui/core/Chip';
import Badge from '@material-ui/core/Badge';
import Divider from '@material-ui/core/Divider';
import Tooltip from '@material-ui/core/Tooltip';
import InputAdornment from '@material-ui/core/InputAdornment';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';
import VideoCallIcon from '@material-ui/icons/VideoCall';
import moment from 'moment';
import VideoCallModal from '../../components/VideoCall/VideoCallModal';

const backendBaseUrl = process.env.REACT_APP_BACKEND_URL || 'https://api.virtus-one.com';
const ACTIVE_VIDEO_CALL_SESSION_KEY = 'virtus.videoCall.active';

const isVideoCallAlreadyActive = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return window.sessionStorage.getItem(ACTIVE_VIDEO_CALL_SESSION_KEY) === '1';
  } catch (error) {
    return false;
  }
};

const warnIfVideoCallAlreadyActive = () => {
  if (!isVideoCallAlreadyActive()) {
    return false;
  }

  window.alert('A videocall is already open. Close the current call before starting another one.');
  return true;
};

const useStyles = makeStyles(() => ({
  dialogPaper: {
    borderRadius: 18,
  },
  dialogActionButton: {
    transition: 'background 0.2s',
    backgroundColor: '#e6eaf0',
    color: '#002D55',
    fontWeight: 700,
    fontSize: 'clamp(0.72rem, 0.8vw, 0.88rem)',
    borderRadius: 18,
    border: 'none',
    boxShadow: 'none',
    padding: '6px 16px',
    minWidth: 92,
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#e6eaf0',
      color: '#F79500',
      boxShadow: 'none',
    },
  },
  dialogTitleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  dialogTitleText: {
    fontWeight: 700,
    color: '#002D55',
    fontSize: 'clamp(0.95rem, 1.1vw, 1.05rem)',
  },
  dialogTitleSubtitle: {
    color: '#607086',
    fontSize: 'clamp(0.7rem, 0.78vw, 0.82rem)',
    lineHeight: 1.35,
  },
  dialogContent: {
    paddingTop: 8,
  },
  searchField: {
    marginBottom: 12,
  },
  listItem: {
    borderRadius: 14,
    marginBottom: 8,
    border: '1px solid rgba(0,45,85,0.08)',
    background: '#fff',
  },
  listItemWaiting: {
    borderColor: 'rgba(251,116,26,0.35)',
    background: 'rgba(251,116,26,0.06)',
  },
  testChip: {
    fontWeight: 700,
    color: '#9A3E00',
    background: 'rgba(251,116,26,0.14)',
  },
  listItemTextPrimary: {
    fontWeight: 600,
    color: '#002D55',
    fontSize: 'clamp(0.82rem, 0.92vw, 0.95rem)',
  },
  listItemTextSecondary: {
    color: '#607086',
    fontSize: 'clamp(0.72rem, 0.8vw, 0.82rem)',
  },
  emptyState: {
    padding: '18px 8px',
    textAlign: 'center',
    color: '#607086',
  },
  emptyStateTitle: {
    fontWeight: 600,
    marginBottom: 4,
    color: '#002D55',
    fontSize: 'clamp(0.82rem, 0.92vw, 0.95rem)',
  },
  emptyStateErrorTitle: {
    fontWeight: 600,
    marginBottom: 4,
    color: '#d32f2f',
    fontSize: 'clamp(0.82rem, 0.92vw, 0.95rem)',
  },
  emptyStateBody: {
    fontSize: 'clamp(0.72rem, 0.8vw, 0.82rem)',
    lineHeight: 1.4,
  },
  statusChip: {
    fontWeight: 600,
    fontSize: 'clamp(0.66rem, 0.74vw, 0.78rem)',
  },
  footerCaption: {
    color: '#607086',
    fontSize: 'clamp(0.7rem, 0.78vw, 0.82rem)',
    lineHeight: 1.35,
  },
  headerCaption: {
    color: '#607086',
    fontSize: 'clamp(0.7rem, 0.78vw, 0.82rem)',
    lineHeight: 1.35,
  },
}));

const formatAppointmentTime = (appointment) => {
  const rawDate = appointment?.start_datetime || appointment?.appointment_date || appointment?.startDate;
  return rawDate ? moment(rawDate).format('ddd D MMM · HH:mm') : 'No time';
};

const getAppointmentTitle = (appointment) => (
  appointment?.patient_name
  || appointment?.patient_full_name
  || appointment?.title
  || appointment?.service_name
  || 'Video consultation'
);

const getAppointmentSubtitle = (appointment) => (
  appointment?.service_name
  || appointment?.appointment_type_label
  || appointment?.appointment_type
  || ''
);

const VideoCallLauncherDialog = ({ open, onClose, authToken }) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [appointments, setAppointments] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [videoCallOpen, setVideoCallOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadUpcomingVideoConsultations = async () => {
      if (!open || !authToken) {
        setAppointments([]);
        setError('');
        setLoading(false);
        setSearchQuery('');
        setSelectedAppointment(null);
        setVideoCallOpen(false);
        return;
      }

      setLoading(true);
      setError('');

      try {
        const response = await fetch(
          `${backendBaseUrl}/api/calendar/appointments/upcoming/?appointment_type=video_consultation&include_video_session=1&include_test_patient=1`,
          {
            headers: {
              Authorization: `Token ${authToken}`,
              Accept: 'application/json',
            },
          },
        );
        const payload = await response.json();
        if (!response.ok) {
          throw new Error(payload?.error || 'Failed to load upcoming videoconsultations');
        }

        if (!cancelled) {
          setAppointments(Array.isArray(payload?.appointments) ? payload.appointments : []);
        }
      } catch (fetchError) {
        if (!cancelled) {
          setError(fetchError.message || 'Failed to load upcoming videoconsultations');
          setAppointments([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadUpcomingVideoConsultations();

    return () => {
      cancelled = true;
    };
  }, [authToken, open]);

  const filteredAppointments = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      return appointments;
    }

    return appointments.filter((appointment) => {
      const searchableText = [
        getAppointmentTitle(appointment),
        getAppointmentSubtitle(appointment),
        appointment?.staff_member_name,
        appointment?.staff_name,
        appointment?.patient_name,
        appointment?.status,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return searchableText.includes(query);
    });
  }, [appointments, searchQuery]);

  const handleJoinAppointment = (appointment) => {
    if (warnIfVideoCallAlreadyActive()) {
      return;
    }

    const appointmentId = appointment?.id || appointment?.appointment_id;
    if (!appointmentId) {
      return;
    }

    setSelectedAppointment({
      ...appointment,
      id: appointmentId,
      appointment_id: appointmentId,
    });
    setVideoCallOpen(true);
  };

  const handleCloseVideoCall = () => {
    setVideoCallOpen(false);
    setSelectedAppointment(null);
  };

  const handleCloseDialog = () => {
    if (onClose) {
      onClose();
    }
    setSearchQuery('');
    setSelectedAppointment(null);
    setVideoCallOpen(false);
  };

  const waitingCount = appointments.filter((appointment) => Boolean(appointment?.video_session?.waiting_room)).length;

  return (
    <>
      <Dialog
        open={open && !videoCallOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ className: classes.dialogPaper }}
      >
        <DialogTitle>
          <Box className={classes.dialogTitleRow}>
            <Box>
              <Typography className={classes.dialogTitleText}>Video call launcher</Typography>
              <Typography className={classes.headerCaption}>
                Upcoming videoconsultations and quick search
              </Typography>
            </Box>
            <IconButton aria-label="close video call launcher" onClick={handleCloseDialog} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <TextField
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Search patient or appointment"
            variant="outlined"
            size="small"
            fullWidth
            className={classes.searchField}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          {loading && (
            <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px 0' }}>
              <CircularProgress size={26} />
            </Box>
          )}

          {!loading && error && (
            <Box className={classes.emptyState}>
              <Typography className={classes.emptyStateErrorTitle}>
                Could not load videoconsultations
              </Typography>
              <Typography className={classes.emptyStateBody}>{error}</Typography>
            </Box>
          )}

          {!loading && !error && filteredAppointments.length === 0 && (
            <Box className={classes.emptyState}>
              <Typography className={classes.emptyStateTitle}>
                No upcoming videoconsultations found
              </Typography>
              <Typography className={classes.emptyStateBody}>
                {searchQuery ? 'Try a broader search term.' : 'Use the edit booking icon to join a booked call.'}
              </Typography>
            </Box>
          )}

          {!loading && !error && filteredAppointments.length > 0 && (
            <List disablePadding>
              {filteredAppointments.map((appointment) => {
                const waitingRoom = Boolean(appointment?.video_session?.waiting_room);
                const statusLabel = waitingRoom ? 'Waiting' : (appointment?.video_session?.status || appointment?.status || 'Scheduled');

                return (
                  <React.Fragment key={appointment?.id || appointment?.appointment_id}>
                    <ListItem
                      button
                      onClick={() => handleJoinAppointment(appointment)}
                      className={`${classes.listItem} ${waitingRoom ? classes.listItemWaiting : ''}`}
                    >
                      <ListItemText
                        primary={(
                          <Typography className={classes.listItemTextPrimary}>
                            {getAppointmentTitle(appointment)}
                          </Typography>
                        )}
                        secondary={(
                          <Typography className={classes.listItemTextSecondary}>
                            {formatAppointmentTime(appointment)}
                            {getAppointmentSubtitle(appointment) ? ` · ${getAppointmentSubtitle(appointment)}` : ''}
                          </Typography>
                        )}
                      />
                      <ListItemSecondaryAction style={{ right: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                        {appointment?.is_test_patient && (
                          <Chip
                            size="small"
                            label="Test"
                            className={classes.testChip}
                          />
                        )}
                        <Chip
                          size="small"
                          label={statusLabel}
                          color={waitingRoom ? 'secondary' : 'default'}
                          className={classes.statusChip}
                        />
                        <Tooltip title={waitingRoom ? 'Patient is waiting' : 'Join videocall'}>
                          <Badge color="secondary" variant="dot" overlap="circular" invisible={!waitingRoom}>
                            <VideoCallIcon style={{ color: '#002D55' }} />
                          </Badge>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    </ListItem>
                    <Divider component="li" />
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </DialogContent>
        <DialogActions style={{ justifyContent: 'space-between', padding: '12px 24px 20px' }}>
              <Typography className={classes.footerCaption}>
            {waitingCount > 0 ? `${waitingCount} patient${waitingCount === 1 ? '' : 's'} waiting now` : 'No patients currently waiting'}
          </Typography>
          <Button onClick={handleCloseDialog} variant="contained" className={classes.dialogActionButton}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {videoCallOpen && selectedAppointment && (
        <VideoCallModal
          open={videoCallOpen}
          appointment={selectedAppointment}
          authToken={authToken}
          onClose={handleCloseVideoCall}
        />
      )}
    </>
  );
};

export default VideoCallLauncherDialog;
